import React, { useEffect, useRef, useState } from "react"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import Button from "aldoo-ra/Button"
import { CheckCircleIcon } from "@heroicons/react/24/solid"
import { acceptInvite, activateOrg } from "aldoo-ra/Organizations/api"
import Spinner from "../ui/Spinner"
import useT from "../hooks/useT"
import useSearchParams from "../hooks/useSearchParams"
import { usePageTitle } from "../hooks/usePageTitle"
import { requestSignIn } from "../utils/signIn"

/**
 * /join-org?invite=<hash> — where an organization invitation mail lands.
 *
 * The invite is accepted against the framework (`organizations/invite/accept`).
 * An invitation sent to an address carries its own account: the API answers
 * with a session for it, which is stored here, so the invitee is signed in by
 * the time they read the page. A share link cannot mint an account — it
 * answers `signin_required`, and the page sends them through the sign-in and
 * back to the same url.
 */
export default function JoinOrgPage() {
  const t = useT()
  const params = useSearchParams()
  const [auth, setAuth] = PlatformAuth.useAuth()
  const token = params.get("invite")
  const [state, setState] = useState({ status: "pending" })
  // one accept per (invite, session): a sign-in from the "sign in to join"
  // step accepts again with the new session (as in Aldoo Meet), and React's
  // development double-mount does not spend the invite twice
  const attemptedFor = useRef(null)
  const joined = useRef(false)
  usePageTitle(t`Join organization`)

  useEffect(() => {
    if (!token) return setState({ status: "error", error: "token_required" })
    // done is done: the session an email invite brings would re-arm it
    if (joined.current) return
    const key = `${token}:${auth?.auth_token || ""}`
    if (attemptedFor.current === key) return
    attemptedFor.current = key

    const browserDevice = typeof window === "undefined" ? undefined : localStorage.getItem("deviceID") || undefined
    acceptInvite(token, browserDevice)
      .then((res) => {
        if (res?.result) {
          joined.current = true
          // an email invite answers with the session of the invited account:
          // the invitee is signed in from here on
          if (res.auth_token) setAuth({ email: res.email, deviceID: res.deviceID, _id: res.userId, auth_token: res.auth_token })
          activateOrg(res.orgId)
          return setState({ status: "joined", orgName: res.orgName, orgId: res.orgId })
        }
        setState({ status: res?.error === "signin_required" ? "signin" : "error", error: res?.error, orgName: res?.orgName, alreadyAccepted: !!res?.alreadyAccepted })
      })
      .catch(() => {
        attemptedFor.current = null
        setState({ status: "error", error: "request_failed" })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, auth?.auth_token])

  const ERRORS = {
    token_required: t`This link carries no invitation.`,
    invalid_invite: t`This invitation is no longer valid.`,
    invite_expired: t`This invitation has expired. Ask for a new one.`,
    login_failed: t`The account could not be signed in. Try again.`,
    request_failed: t`The invitation could not be read. Try again.`,
  }

  return (
    <div className="w-full flex flex-col items-center px-4 py-24 gap-6 text-center">
      {state.status === "pending" && <Spinner className="py-10" />}

      {state.status === "joined" && (
        <>
          <CheckCircleIcon className="w-16 h-16 text-primary" />
          <h1 className="m-0 text-[32px] md:text-[44px] font-bold">
            {state.orgName ? t("You joined %org%", state.orgName) : t`You joined the organization`}
          </h1>
          <p className="m-0 max-w-[480px] text-text_secondary text-[16px] leading-relaxed">
            {t`Your account is signed in. The organization's products and orders are in the vendor portal.`}
          </p>
          {/* a member works in the vendor portal, on the organization joined;
              the admin is the platform's (role:admin), not an organization's */}
          <Button variant="primary_l" href={state.orgId ? `/vendor?org=${encodeURIComponent(state.orgId)}` : "/vendor"} data-testid="join-open-portal">
            {t`Open the vendor portal`}
          </Button>
        </>
      )}

      {state.status === "signin" && (
        <>
          <h1 className="m-0 text-[32px] md:text-[44px] font-bold">
            {state.orgName ? t("Join %org%", state.orgName) : t`Join the organization`}
          </h1>
          <p className="m-0 max-w-[480px] text-text_secondary text-[16px] leading-relaxed">
            {state.alreadyAccepted ? t`You already accepted this invitation. Sign in to open it.` : t`Sign in with your Aldoo account to accept this invitation.`}
          </p>
          <Button
            variant="primary_l"
            onClick={() => requestSignIn()}
          >
            {t`Sign In`}
          </Button>
        </>
      )}

      {state.status === "error" && (
        <>
          <h1 className="m-0 text-[32px] md:text-[44px] font-bold">{t`Invitation not available`}</h1>
          <p className="m-0 max-w-[480px] text-text_secondary text-[16px] leading-relaxed">
            {ERRORS[state.error] || ERRORS.request_failed}
          </p>
          <Button variant="link" href="/">{t`Back to the store`}</Button>
        </>
      )}
    </div>
  )
}
